import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, mergeClasses, shorthands, Checkbox, Spinner } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  MailInbox20Regular,
  Search20Regular,
  Dismiss16Regular,
  BranchCompare20Regular,
  PersonArrowRight20Regular,
  CheckmarkCircle20Regular,
  Building20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { useSmartBack } from "../hooks/smartBack";
import { ConfirmDialog } from "../components/redesign/ConfirmDialog";
import { bestaetigen, hinweisen } from "../components/redesign/bestaetigen";
import { KontaktSuchfeld, type KontaktTreffer } from "../components/forms/widgets/KontaktSuchfeld";
import {
  fetchOutlookReiter,
  listOutlookImporte,
  outlookFreigeben,
  outlookZuFirma,
  outlookMergeLink,
  type OutlookImportZeile,
  type OutlookListe,
  type OutlookPendant,
  type OutlookReiter,
} from "../services/outlookImporteService";

/**
 * Datenbereinigung „Outlook-Importe“ (User 02.10.2026).
 *
 * Importierte Outlook-Kontakte fehlen in Kontakttabelle und Suche, bis sie hier
 * bereinigt sind. Je Quelle ein Reiter (DC und MK nur für den Besitzer und
 * admin, die gemeinsamen Kontakte für admin und gfgl), darin drei Listen:
 *  - mit Pendant: bestehender Kontakt gefunden → Zusammenführen (Sieger ist
 *    immer der bestehende Kontakt);
 *  - ohne Pendant: Zuweisen (Suche, auch abweichende Schreibweisen) oder
 *    Importieren (Freigabe, auch als Mehrfachauswahl);
 *  - Sonderzeichen im Namen.
 * Jede Zeile lässt sich außerdem in eine Firma umwandeln.
 */

const PAGE_SIZE = 30;

const LISTEN: Array<{ key: OutlookListe; label: string; hinweis: string }> = [
  {
    key: "pendant",
    label: "Mit Pendant in KMVS",
    hinweis: "Zu diesen Outlook-Kontakten gibt es schon einen Kontakt (gleiche E-Mail, Telefonnummer oder gleicher Name). Zusammenführen: der bestehende Kontakt bleibt.",
  },
  {
    key: "ohne",
    label: "Ohne Pendant",
    hinweis: "Kein passender Kontakt gefunden. Gibt es ihn doch (anders geschrieben)? Dann zuweisen — sonst importieren.",
  },
  {
    key: "sonderzeichen",
    label: "Sonderzeichen im Namen",
    hinweis: "Namen mit ungewöhnlichen Zeichen (z. B. Klammern, Anführungszeichen, Ziffern-Reste). Name im Kontakt korrigieren, zuweisen oder importieren.",
  },
];

const useStyles = makeStyles({
  root: {
    ...shorthands.padding("24px", "32px"),
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1180px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    ...shorthands.padding("20px", "24px"),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
  },
  goldLine: { height: "3px", width: "60px", backgroundColor: BRAND.gold, ...shorthands.borderRadius("999px") },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  tabs: { display: "flex", columnGap: "8px", flexWrap: "wrap", rowGap: "8px" },
  tab: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ...shorthands.padding("8px", "16px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 600,
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
  },
  tabAktiv: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { ...shorthands.borderColor(BRAND.ink) },
  },
  tabZahl: {
    fontSize: "0.75rem",
    ...shorthands.padding("1px", "8px"),
    ...shorthands.borderRadius("999px"),
    backgroundColor: "rgba(247,168,35,0.25)",
  },
  listen: {
    display: "flex",
    columnGap: "4px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  listeBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    ...shorthands.borderBottom("3px", "solid", "transparent"),
    ...shorthands.padding("8px", "14px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.muted,
    fontWeight: 600,
    ":hover": { color: BRAND.ink },
  },
  listeAktiv: { color: BRAND.purple, ...shorthands.borderBottom("3px", "solid", BRAND.gold) },
  hinweis: { color: BRAND.muted, fontSize: "0.85rem" },
  toolbar: { display: "flex", alignItems: "center", columnGap: "10px", flexWrap: "wrap", rowGap: "8px" },
  searchRow: {
    flexGrow: 1,
    minWidth: "260px",
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("8px", "12px"),
  },
  searchInput: {
    flexGrow: 1,
    ...shorthands.border("none"),
    outlineStyle: "none",
    backgroundColor: "transparent",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
  },
  iconBtn: {
    display: "inline-flex",
    ...shorthands.padding("4px"),
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    color: BRAND.muted,
    cursor: "pointer",
    ...shorthands.borderRadius("999px"),
  },
  countChip: {
    fontSize: "0.8rem",
    color: BRAND.purple,
    backgroundColor: "#f0e6f2",
    ...shorthands.padding("2px", "10px"),
    ...shorthands.borderRadius("999px"),
    fontWeight: 600,
  },
  aktion: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "14px"),
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    ":hover": { backgroundColor: "#f0e6f2" },
    ":disabled": { opacity: 0.5, cursor: "default" },
  },
  aktionVoll: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ":hover": { backgroundColor: BRAND.ink },
  },
  scrollBox: {
    height: "calc(100vh - 400px)",
    minHeight: "260px",
    overflowY: "auto",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    backgroundColor: BRAND.white,
  },
  zeile: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "12px",
    ...shorthands.padding("12px", "16px"),
    ...shorthands.borderBottom("1px", "solid", BRAND.bg),
  },
  spalte: { display: "flex", flexDirection: "column", rowGap: "3px", minWidth: 0, flexGrow: 1, flexBasis: 0 },
  label: {
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    alignSelf: "flex-start",
    ...shorthands.padding("1px", "8px"),
    ...shorthands.borderRadius("999px"),
  },
  labelOutlook: { color: "#8a5a00", backgroundColor: "#fdf1dc" },
  labelBestehend: { color: "#1b7f4d", backgroundColor: "#e6f4ec" },
  name: { fontFamily: BRAND.fontAccent, fontSize: "1rem", color: BRAND.ink, overflowWrap: "anywhere" },
  meta: { color: BRAND.muted, fontSize: "0.8rem", overflowWrap: "anywhere" },
  badge: {
    fontSize: "0.7rem",
    color: BRAND.muted,
    backgroundColor: BRAND.bg,
    ...shorthands.padding("1px", "8px"),
    ...shorthands.borderRadius("999px"),
    marginLeft: "8px",
  },
  pendant: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    ...shorthands.padding("6px", "10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#fbfcfb",
  },
  grund: { fontSize: "0.72rem", color: BRAND.purple, fontWeight: 600 },
  aktionen: { display: "flex", flexDirection: "column", rowGap: "6px", flexShrink: 0, alignItems: "stretch" },
  leer: { ...shorthands.padding("28px", "16px"), textAlign: "center", color: BRAND.muted, fontSize: "0.9rem" },
  mehr: { textAlign: "center", color: BRAND.muted, ...shorthands.padding("12px") },
  link: { color: BRAND.purple, cursor: "pointer", textDecoration: "underline" },
});

const anzeigeName = (r: { lastname: string | null; firstName: string | null }) =>
  [r.lastname, r.firstName].filter((x) => x && x.trim()).join(", ") || "(ohne Namen)";

const OutlookImportePage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const goBack = useSmartBack("/datenbereinigung");
  const [params, setParams] = useSearchParams();

  const [reiter, setReiter] = useState<OutlookReiter[] | null>(null);
  const quelle = params.get("reiter") || reiter?.[0]?.quelle || "";
  const liste = (LISTEN.find((l) => l.key === params.get("liste"))?.key ?? "pendant") as OutlookListe;

  const ladeReiter = useCallback(() => {
    fetchOutlookReiter()
      .then(setReiter)
      .catch(() => setReiter([]));
  }, []);
  useEffect(ladeReiter, [ladeReiter]);

  const setzeParam = (key: string, wert: string) => {
    const next = new URLSearchParams(params);
    next.set(key, wert);
    if (key === "reiter" && !next.get("liste")) next.set("liste", "pendant");
    setParams(next, { replace: true });
  };

  // --- Liste: Suche + Pagination (Infinite Scroll) ---
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [rows, setRows] = useState<OutlookImportZeile[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(
    async (offset: number) => {
      if (!quelle) return;
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      if (offset === 0) setLoading(true);
      try {
        const { rows: page, total: tot } = await listOutlookImporte(
          quelle,
          liste,
          qDebounced || undefined,
          PAGE_SIZE,
          offset,
          ac.signal,
        );
        setRows((prev) => (offset === 0 ? page : [...prev, ...page]));
        setTotal(tot);
        setHasMore(offset + page.length < tot);
      } catch (e) {
        if (!(e instanceof DOMException && e.name === "AbortError")) setHasMore(false);
      } finally {
        if (offset === 0 && !ac.signal.aborted) setLoading(false);
      }
    },
    [quelle, liste, qDebounced],
  );

  useEffect(() => {
    setSelected(new Set());
    setRows([]);
    void load(0);
    return () => abortRef.current?.abort();
  }, [load]);

  /** Nach einer Aktion: erledigte Zeilen sofort raus, Zähler nachziehen. */
  const entfernen = (ids: number[]) => {
    const weg = new Set(ids);
    setRows((prev) => prev.filter((r) => !weg.has(r.id)));
    setTotal((t) => Math.max(0, t - ids.length));
    setSelected((prev) => new Set([...prev].filter((id) => !weg.has(id))));
    ladeReiter();
  };

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const alleAn = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const alleUmschalten = () => setSelected(alleAn ? new Set() : new Set(rows.map((r) => r.id)));

  const zurueck = `/datenbereinigung/outlook-importe?reiter=${encodeURIComponent(quelle)}&liste=${liste}`;

  const importieren = async (ids: number[]) => {
    if (!ids.length) return;
    const ok = await bestaetigen({
      titel: ids.length === 1 ? "Kontakt importieren?" : `${ids.length} Kontakte importieren?`,
      text: "Die Kontakte erscheinen danach in der Kontakttabelle und in der Suche. Persönliche Kontakte bleiben persönlich.",
      ja: "Importieren",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await outlookFreigeben(quelle, ids);
      entfernen(ids);
    } catch (e: any) {
      await hinweisen({ titel: "Importieren fehlgeschlagen", text: String(e?.message ?? e), ton: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const zuFirma = async (ids: number[]) => {
    if (!ids.length) return;
    const ok = await bestaetigen({
      titel: ids.length === 1 ? "In Firma umwandeln?" : `${ids.length} Kontakte in Firmen umwandeln?`,
      text: "Vor- und Nachname werden zum Firmennamen zusammengesetzt (z. B. „Generali Deutschland“ + „AG“). Personen-Status wie „Ansprechpartner Produktpartner“ werden zu „Produktpartner“. Der Kontakt bleibt in dieser Liste, bis er importiert oder zusammengeführt ist.",
      ja: "Umwandeln",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await outlookZuFirma(quelle, ids);
      void load(0);
    } catch (e: any) {
      await hinweisen({ titel: "Umwandeln fehlgeschlagen", text: String(e?.message ?? e), ton: "danger" });
    } finally {
      setBusy(false);
    }
  };

  // --- Zuweisen: bestehenden Kontakt suchen → Merge-Seite ---
  const [zuweisenFuer, setZuweisenFuer] = useState<OutlookImportZeile | null>(null);
  const [ziel, setZiel] = useState<KontaktTreffer | null>(null);
  const zusammenfuehren = (bestehendId: number, importId: number) =>
    navigate(outlookMergeLink(bestehendId, importId, zurueck));

  const aktiveListe = LISTEN.find((l) => l.key === liste)!;
  const ausgewaehlt = useMemo(() => [...selected], [selected]);

  const renderPendant = (r: OutlookImportZeile, p: OutlookPendant) => (
    <div key={p.id} className={styles.pendant}>
      <div className={styles.spalte}>
        <span className={mergeClasses(styles.label, styles.labelBestehend)}>Bestehender Kontakt</span>
        <span>
          <a className={styles.link} href={`/contacts/${p.id}`} target="_blank" rel="noreferrer">
            {anzeigeName(p)}
          </a>
          {p.typ && <span className={styles.badge}>{p.typ}</span>}
          {p.status && <span className={styles.badge}>{p.status}</span>}
        </span>
        <span className={styles.meta}>
          {[p.contactNbrInt ? `Nr. ${p.contactNbrInt}` : null, p.anschrift].filter(Boolean).join(" · ")}
        </span>
        <span className={styles.grund}>Gleich: {p.gruende.join(", ")}</span>
      </div>
      <button className={mergeClasses(styles.aktion, styles.aktionVoll)} onClick={() => zusammenfuehren(p.id, r.id)}>
        <BranchCompare20Regular style={{ fontSize: 16 }} /> Zusammenführen
      </button>
    </div>
  );

  const renderZeile = (r: OutlookImportZeile) => (
    <div key={r.id} className={styles.zeile}>
      <Checkbox checked={selected.has(r.id)} onChange={() => toggle(r.id)} aria-label="Kontakt auswählen" />
      <div className={styles.spalte}>
        <span className={mergeClasses(styles.label, styles.labelOutlook)}>Outlook-Kontakt</span>
        <span>
          <a className={styles.name} href={`/contacts/${r.id}`} target="_blank" rel="noreferrer">
            {anzeigeName(r)}
          </a>
          {r.typ && <span className={styles.badge}>{r.typ}</span>}
          {r.status && <span className={styles.badge}>{r.status}</span>}
        </span>
        <span className={styles.meta}>
          {[r.email, r.telefon, r.anschrift, r.employer].filter(Boolean).join(" · ") || "keine Kontaktdaten"}
        </span>
      </div>
      {r.pendants.length > 0 && (
        <div className={styles.spalte} style={{ rowGap: 6 }}>
          {r.pendants.map((p) => renderPendant(r, p))}
        </div>
      )}
      <div className={styles.aktionen}>
        <button className={styles.aktion} disabled={busy} onClick={() => { setZiel(null); setZuweisenFuer(r); }}>
          <PersonArrowRight20Regular style={{ fontSize: 16 }} /> Zuweisen
        </button>
        <button className={styles.aktion} disabled={busy} onClick={() => importieren([r.id])}>
          <CheckmarkCircle20Regular style={{ fontSize: 16 }} /> Importieren
        </button>
        {r.typ !== "Firma" && (
          <button className={styles.aktion} disabled={busy} onClick={() => zuFirma([r.id])}>
            <Building20Regular style={{ fontSize: 16 }} /> In Firma umwandeln
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={goBack}>
        <ArrowLeft20Regular /> Zurück zur Datenbereinigung
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <MailInbox20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Outlook-Importe</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Importierte Outlook-Kontakte erscheinen erst in der Kontakttabelle, wenn sie hier zusammengeführt,
          zugewiesen oder importiert sind.
        </span>
      </div>

      {reiter === null ? (
        <Spinner size="small" label="Lade Outlook-Importe…" />
      ) : reiter.length === 0 ? (
        <p className={styles.leer}>Für dich liegen keine Outlook-Importe zur Bereinigung vor.</p>
      ) : (
        <>
          <div className={styles.tabs} role="tablist">
            {reiter.map((t) => (
              <button
                key={t.quelle}
                role="tab"
                aria-selected={t.quelle === quelle}
                className={mergeClasses(styles.tab, t.quelle === quelle && styles.tabAktiv)}
                onClick={() => setzeParam("reiter", t.quelle)}
              >
                {t.label}
                <span className={styles.tabZahl}>{t.offen}</span>
              </button>
            ))}
          </div>

          <div className={styles.listen} role="tablist">
            {LISTEN.map((l) => (
              <button
                key={l.key}
                role="tab"
                aria-selected={l.key === liste}
                className={mergeClasses(styles.listeBtn, l.key === liste && styles.listeAktiv)}
                onClick={() => setzeParam("liste", l.key)}
              >
                {l.label}
              </button>
            ))}
          </div>
          <span className={styles.hinweis}>{aktiveListe.hinweis}</span>

          <div className={styles.toolbar}>
            <div className={styles.searchRow}>
              <Search20Regular style={{ color: BRAND.muted }} />
              <input
                className={styles.searchInput}
                type="search"
                placeholder="In dieser Liste suchen (Name, E-Mail, Telefon) …"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {q && (
                <button className={styles.iconBtn} onClick={() => setQ("")} aria-label="Suche zurücksetzen">
                  <Dismiss16Regular />
                </button>
              )}
            </div>
            <span className={styles.countChip}>
              {total} {total === 1 ? "Kontakt" : "Kontakte"}
            </span>
            {rows.length > 0 && (
              <Checkbox checked={alleAn} onChange={alleUmschalten} label="Alle geladenen auswählen" />
            )}
            {ausgewaehlt.length > 0 && (
              <>
                <button
                  className={mergeClasses(styles.aktion, styles.aktionVoll)}
                  disabled={busy}
                  onClick={() => importieren(ausgewaehlt)}
                >
                  <CheckmarkCircle20Regular style={{ fontSize: 16 }} /> {ausgewaehlt.length} importieren
                </button>
                <button className={styles.aktion} disabled={busy} onClick={() => zuFirma(ausgewaehlt)}>
                  <Building20Regular style={{ fontSize: 16 }} /> In Firma umwandeln
                </button>
              </>
            )}
          </div>

          <div id="scrollOutlookImporte" className={styles.scrollBox}>
            {loading ? (
              <p className={styles.leer}>Lade Kontakte…</p>
            ) : rows.length === 0 ? (
              <p className={styles.leer}>{qDebounced ? "Kein Kontakt passt zur Suche." : "In dieser Liste ist nichts mehr offen."}</p>
            ) : (
              <InfiniteScroll
                dataLength={rows.length}
                next={() => void load(rows.length)}
                hasMore={hasMore}
                loader={<div className={styles.mehr}>Lade weitere Kontakte…</div>}
                scrollableTarget="scrollOutlookImporte"
                style={{ overflow: "visible" }}
              >
                {rows.map(renderZeile)}
              </InfiniteScroll>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={zuweisenFuer !== null}
        title="Bestehendem Kontakt zuweisen"
        message={
          <div style={{ display: "flex", flexDirection: "column", rowGap: 10 }}>
            <span>
              Outlook-Kontakt <strong>{zuweisenFuer ? anzeigeName(zuweisenFuer) : ""}</strong> gehört zu einem
              bestehenden Kontakt? Suche ihn (auch abweichende Schreibweisen werden gefunden). Danach öffnet sich die
              Zusammenführung, der bestehende Kontakt bleibt.
            </span>
            <KontaktSuchfeld
              label="Bestehender Kontakt"
              name="outlookZuweisenZiel"
              placeholder={zuweisenFuer?.lastname ?? "Name, Firma oder Nummer"}
              value={ziel}
              onChange={setZiel}
              autoFocus
              ausblendenIds={zuweisenFuer ? [zuweisenFuer.id] : undefined}
            />
          </div>
        }
        confirmLabel="Weiter zum Zusammenführen"
        cancelLabel="Abbrechen"
        focusConfirm={false}
        onConfirm={() => {
          if (!ziel || !zuweisenFuer) return;
          const imp = zuweisenFuer.id;
          setZuweisenFuer(null);
          zusammenfuehren(ziel.contactId, imp);
        }}
        onCancel={() => setZuweisenFuer(null)}
      />
    </div>
  );
};

export default OutlookImportePage;
